import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { BillingNudgeKey } from "~/features/billing/nudges";
import { useApiClient } from "~/hooks/use-api-client";
import { trackEvent } from "~/lib/analytics";

interface BillingNudgeStatusResponse {
    key: BillingNudgeKey;
    dismissed: boolean;
    cycleStartAt: string;
}

export function useBillingNudge(params: {
    key: BillingNudgeKey;
    enabled?: boolean;
}) {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    const statusQuery = useQuery({
        queryKey: ["billing", "nudge", params.key],
        queryFn: async () => {
            const response = await apiClient.get("/billing/nudge", {
                params: { key: params.key },
            });

            if (response.status === 401) {
                return {
                    key: params.key,
                    dismissed: false,
                    cycleStartAt: new Date(0).toISOString(),
                } satisfies BillingNudgeStatusResponse;
            }

            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                throw new Error(
                    typeof data?.error === "string"
                        ? data.error
                        : "Failed to load billing nudge status",
                );
            }

            return (await response.json()) as BillingNudgeStatusResponse;
        },
        staleTime: 30_000,
        enabled: params.enabled ?? true,
    });

    const dismissMutation = useMutation({
        mutationFn: async () => {
            const response = await apiClient.post("/billing/nudge", {
                key: params.key,
            });
            const payload = await response
                .json()
                .catch(() => ({ error: "Failed to dismiss nudge" }));

            if (!response.ok) {
                throw new Error(
                    typeof payload?.error === "string"
                        ? payload.error
                        : "Failed to dismiss nudge",
                );
            }
        },
        onSuccess: () => {
            trackEvent("clicked button", {
                text: `billing nudge dismissed (${params.key})`,
            });
            queryClient.invalidateQueries({
                queryKey: ["billing", "nudge", params.key],
            });
        },
    });

    return {
        isDismissed: statusQuery.data?.dismissed ?? false,
        isLoading: statusQuery.isLoading,
        isError: statusQuery.isError,
        dismiss: dismissMutation.mutateAsync,
        isDismissing: dismissMutation.isPending,
    };
}
